import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { listSentencesByChapter } from '@dataconnect/generated';
import { FaArrowLeft, FaArrowRight, FaVolumeUp, FaVolumeMute, FaPlay } from 'react-icons/fa';
import { normalizeText } from '../utilities/textNormalization';
import DisplaySentence from '../utilities/ui/DisplaySentence';
import { 
  shouldPlayLanguageAudio, 
  shouldPlayEnglishAudio,
  getLanguageVolume,
  getEnglishVolume 
} from '../utilities/settingsHelper';

const ChapterViewer = ({ selectedChapter, student, onBack, onLogout }) => { // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sentences, setSentences] = useState([]);
  const [index, setIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(0); // eslint-disable-line no-unused-vars
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const langAudioRef = useRef(null);
  const engAudioRef = useRef(null);
  const urlCacheRef = useRef(new Map()); // storagePath → resolved download URL

  const [showCompletion, setShowCompletion] = useState(false);

  const hasPrev = index > 0;
  const hasNext = index < sentences.length - 1;

  const progressPct = useMemo(() => {
    if (!sentences.length) return 0;
    return Math.round(((index + 1) / sentences.length) * 100);
  }, [index, sentences.length]);
  const storage = getStorage();

  // Resolve a Firebase Storage path to a download URL, caching the result.
  const resolveStorageUrl = async (storagePath) => {
    if (!storagePath) return null;
    if (urlCacheRef.current.has(storagePath)) return urlCacheRef.current.get(storagePath);
    const url = await getDownloadURL(ref(storage, storagePath));
    urlCacheRef.current.set(storagePath, url);
    return url;
  };

  // Load image URL from Firebase Storage (cache-first)
  const loadImageUrl = async (imagePath) => {
    if (!imagePath) { setImageUrl(null); return; }
    setImageLoading(true);
    try {
      const url = await resolveStorageUrl(imagePath);
      setImageUrl(url);
    } catch (error) {
      setImageUrl(null);
    } finally {
      setImageLoading(false);
    }
  };

  const normalizeSentences = (res) => {
    const items = Array.isArray(res)
      ? res
      : Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res?.data?.sentences)
          ? res.data.sentences
          : [];

    // Sort and normalize text for proper display
    return [...items]
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .map(item => ({
        ...item,
        languageText: normalizeText(item.languageText),
        englishText: normalizeText(item.englishText)
      }));
  };

  async function refresh() {
    if (!selectedChapter?.id) return;
    setLoading(true);
    setErr(null);
    urlCacheRef.current.clear();
    try {
      const res = await listSentencesByChapter({ "chapterId": selectedChapter?.id });
      const items = normalizeSentences(res);
      setSentences(items);
      setIndex(0);
    } catch (e) {
      setErr(e.message || 'Failed to load chapter');
    } finally {
      setLoading(false);
    }
  }

  function getImageUrl(url) { // eslint-disable-line no-unused-vars
    return url;
  }

  useEffect(() => {
    refresh();
  }, [selectedChapter?.id]);

  // Load image when sentence changes
  useEffect(() => {
    if (sentences.length > 0 && sentences[index]) {
      const currentSentence = sentences[index];

      // Stop any currently playing audio
      stopPlayingAudio();

      // Load image
      loadImageUrl(currentSentence.imageUrl);

      // Preload the next 3 sentences in the background
      preloadAhead(index, 3);
    }
  }, [index, sentences, student]);

  const goPrev = useCallback(() => hasPrev && setIndex((i) => i - 1), [hasPrev]);
  const goNext = useCallback(() => {
    if (hasNext) {
      setIndex((i) => i + 1);
    } else {
      // Last sentence - show completion
      setShowCompletion(true);
      setTimeout(() => {
        onBack();
      }, 2000);
    }
  }, [hasNext, onBack]);
  const exit = useCallback(() => onBack());

  function stopPlayingAudio() {
    if (langAudioRef.current) { langAudioRef.current.pause(); langAudioRef.current.currentTime = 0; }
    if (engAudioRef.current) { engAudioRef.current.pause(); engAudioRef.current.currentTime = 0; }
    // langAudioRef.removeEventListener('ended', onEnd)
  }

  // Preload the next `lookahead` sentences in the background:
  // resolves storage download URLs, primes the browser's image and audio buffers.
  function preloadAhead(fromIndex, lookahead = 3) {
    for (let i = fromIndex + 1; i <= fromIndex + lookahead && i < sentences.length; i++) {
      const s = sentences[i];
      if (!s) continue;

      if (s.imageUrl && !urlCacheRef.current.has(s.imageUrl)) {
        resolveStorageUrl(s.imageUrl).then(url => {
          if (url) { const img = new Image(); img.src = url; }
        }).catch(() => {});
      }

      for (const audioPath of [s.languageAudioUrl, s.englishAudioUrl].filter(Boolean)) {
        if (!urlCacheRef.current.has(audioPath)) {
          resolveStorageUrl(audioPath).then(url => {
            if (url) { const audio = new Audio(); audio.preload = 'auto'; audio.src = url; }
          }).catch(() => {});
        }
      }
    }
  }

  // Keyboard shortcuts: ← → Esc
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape') exit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext, exit]);

  const s = sentences[index];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
          <div
            className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
          ></div>
        </div>

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <div className="w-5 h-5 text-red-600 mr-3">
                ⚠️
              </div>
              <div className="text-red-800">{err}</div>
            </div>
          </div>
        )}

        {/* Modern Card Viewer */}
        <div className="flex justify-center">
          <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            {/* Content */}
            <div className="p-8">
              {showCompletion ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="text-6xl mb-6">🎉</div>
                  <h2 className="text-3xl font-bold text-green-600 mb-4">Chapter Completed!</h2>
                  <p className="text-gray-600 text-lg mb-6">Great job! You&apos;ve finished reading this chapter.</p>
                  <div className="flex items-center space-x-3 text-gray-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span>Returning to activities...</span>
                  </div>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="text-gray-600 text-lg">Loading sentences…</span>
                  </div>
                </div>
              ) : sentences.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-6xl mb-4">📚</div>
                  <div className="text-gray-500 text-lg">No content available yet.</div>
                </div>
              ) : (
                <div className="flex flex-col min-h-[500px]">
                  <div className={`flex-grow flex flex-col justify-center space-y-8 ${s?.imageUrl ? 'mb-8' : ''}`}>
                    {/* Image */}
                    {s?.imageUrl && (
                      <div className="flex justify-center">
                        {imageLoading ? (
                          <div className="flex items-center justify-center bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 w-full max-w-2xl h-60">
                            <div className="text-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                              <span className="text-gray-500">Loading image...</span>
                            </div>
                          </div>
                        ) : imageUrl ? (
                          <div className="relative group">
                            <img
                              src={imageUrl}
                              alt="Chapter content"
                              className="rounded-xl shadow-lg max-h-80 object-contain transition-transform duration-300 group-hover:scale-105"
                              onLoad={() => setImageLoaded(prev => prev + 1)}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center bg-red-50 rounded-xl border-2 border-dashed border-red-300 w-full max-w-2xl h-60">
                            <div className="text-center">
                              <div className="text-4xl mb-2">⚠️</div>
                              <span className="text-red-500">Failed to load image</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                      {/* Text Content */}
                    <div className="text-center space-y-4">
                      {/* Language text */}
                      {s?.languageText && (
                        <DisplaySentence
                          text={s.languageText}
                          audioUrl={s.languageAudioUrl}
                          textClassName="text-3xl md:text-4xl font-bold text-gray-900 leading-relaxed"
                          buttonClassName="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                          containerClassName="flex items-center justify-center gap-3"
                        />
                      )}
                      {/* English text */}
                      {s?.englishText && (
                        <DisplaySentence
                          text={s.englishText}
                          audioUrl={s.englishAudioUrl}
                          textClassName="text-lg md:text-xl text-gray-600 leading-relaxed"
                          buttonClassName="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                          containerClassName="flex items-center justify-center gap-3"
                        />
                      )}

                      {/* Hidden audio elements used for preloading if needed */}
                      <audio ref={langAudioRef} preload="auto" style={{ display: 'none' }} />
                      <audio ref={engAudioRef} preload="auto" style={{ display: 'none' }} />
                    </div>
                  </div>

                  {/* Navigation controls */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <button
                      className="flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                      onClick={goPrev}
                      disabled={!hasPrev}
                      title="Previous (←)"
                    >
                      <FaArrowLeft className="w-4 h-4 mr-2" />
                      Previous
                    </button>

                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <div className="px-3 py-1 bg-gray-100 rounded-full">
                        {index + 1} of {sentences.length}
                      </div>
                    </div>

                    <button
                      className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                      onClick={goNext}
                      disabled={!hasNext}
                      title="Next (→)"
                    >
                      Next
                      <FaArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChapterViewer;
